/**
 * LanguageSwitcher.js
 *
 * Reusable component for language selection.
 * Can be used in Navbar, Settings, or anywhere language switching is needed.
 *
 * Usage:
 *   <LanguageSwitcher />
 *   <LanguageSwitcher variant="dropdown" onClose={() => {}} />
 *   <LanguageSwitcher variant="radio" />
 */

import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import navConfig from "../config/navbarConfig";
import { warnDev } from "../utils/logger";

export default function LanguageSwitcher({
  variant = "dropdown", // "dropdown" | "radio" | "pill"
  onClose = null,
  className = "",
}) {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  const languages = navConfig?.languages ?? [
    { code: "en", label: "English" },
    { code: "rw", label: "Kinyarwanda" },
  ];

  const handleChangeLanguage = (code) => {
    i18n.changeLanguage(code);
    // Persist in localStorage
    try {
      localStorage.setItem("language", code);
    } catch (e) {
      warnDev("Failed to save language preference:", e);
    }
    // Also persist in user settings if available (optional)
    // via settingsService or profile update
    if (onClose) onClose();
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    if (variant !== "dropdown" || !isOpen) return;

    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, variant]);

  if (variant === "dropdown") {
    return (
      <div className={`relative ${className}`} ref={ref}>
        <button
          onClick={() => setIsOpen((s) => !s)}
          className="flex items-center gap-2 px-3 py-2 rounded-md bg-gray-100 dark:bg-neutral-700 hover:bg-gray-200 dark:hover:bg-neutral-600 text-gray-900 dark:text-gray-100 transition"
          aria-haspopup="true"
          aria-expanded={isOpen}
          title={t ? t("pages.settings.language") : "Language"}
        >
          <Globe size={16} />
          <span className="font-semibold text-sm">
            {(i18n.language || "en").toUpperCase().slice(0, 2)}
          </span>
        </button>

        {isOpen && (
          <div className="absolute top-full right-0 mt-2 w-48 bg-white dark:bg-neutral-800 text-gray-900 dark:text-gray-100 rounded-lg shadow-lg border border-gray-200 dark:border-neutral-700 z-50 overflow-hidden">
            {languages.map(({ code, label }) => (
              <button
                key={code}
                onClick={() => handleChangeLanguage(code)}
                className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-neutral-700 flex items-center justify-between transition"
              >
                <span>{label}</span>
                {i18n.language && i18n.language.startsWith(code) && (
                  <Check size={16} className="text-green-600" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (variant === "radio") {
    return (
      <div className={`space-y-2 ${className}`}>
        <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
          {t ? t("pages.settings.language") : "Language"}
        </label>
        {languages.map(({ code, label }) => (
          <label
            key={code}
            className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-700 transition"
          >
            <input
              type="radio"
              name="language"
              value={code}
              checked={i18n.language && i18n.language.startsWith(code)}
              onChange={() => handleChangeLanguage(code)}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
            />
            <span className="text-gray-700 dark:text-gray-200">{label}</span>
          </label>
        ))}
      </div>
    );
  }

  if (variant === "pill") {
    return (
      <div className={`flex gap-2 ${className}`}>
        {languages.map(({ code, label }) => (
          <button
            key={code}
            onClick={() => handleChangeLanguage(code)}
            className={`px-4 py-2 rounded-full font-semibold transition ${
              i18n.language && i18n.language.startsWith(code)
                ? "bg-green-600 text-white"
                : "bg-gray-200 dark:bg-neutral-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-neutral-600"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    );
  }

  return null;
}
